import type { ProfileCompletionResult } from '../../utils/profileCompletionScore';
import { computeProfileCompletionScore } from '../../utils/profileCompletionScore';
import type { OnboardingDraft, ScreeningPromptItem } from './types';

export function profileStrengthLabel(percent: number): string {
  if (percent >= 85) return 'Strong';
  if (percent >= 60) return 'Good';
  return 'Building';
}

export function buildUserForOnboardingScoring(
  sessionUser: Record<string, unknown> | null,
  draft: OnboardingDraft,
  targetRole: string,
  linkedin: string,
  resumeUrl?: string
): Record<string, unknown> {
  return {
    ...(sessionUser || {}),
    firstName: draft.firstName || sessionUser?.firstName || sessionUser?.fname,
    lastName: draft.lastName || sessionUser?.lastName || sessionUser?.lname,
    city: draft.location?.city || sessionUser?.city,
    state: draft.location?.state || sessionUser?.state,
    country: draft.location?.country || sessionUser?.country,
    linkedin: linkedin || draft.contact?.linkedin || sessionUser?.linkedin,
    resume: resumeUrl || sessionUser?.resume,
    jobTitle: targetRole || draft.targetRole,
    roleTitle: targetRole || draft.targetRole,
  };
}

export function buildPortfolioForOnboardingScoring(draft: OnboardingDraft) {
  const organizations = (draft.workExperience || []).map((item) => ({
    name: item.company || item.title || '',
    description: item.description || '',
  }));

  return {
    overview: draft.overview || '',
    projects: draft.projects || [],
    accomplishments: [...(draft.accomplishments || [])],
    certifications: draft.certifications || [],
    organizations,
    references: [],
    languages: [],
    workCulture: [],
  };
}

export function buildPromptsForOnboardingScoring(
  promptItems: ScreeningPromptItem[],
  videoFiles: Record<number, File>
) {
  return promptItems.map((item, index) => {
    const hasVideo =
      Boolean((item.fileUrl || '').trim() && item.fileUrl !== 'N/A') || Boolean(videoFiles[index]);
    return {
      fileUrl: hasVideo ? item.fileUrl || 'draft://video' : '',
      description: item.description || '',
      supportedDocumentUrl: '',
    };
  });
}

export function resolveOnboardingProfileStrength(
  sessionUser: Record<string, unknown> | null,
  draft: OnboardingDraft | null,
  targetRole: string,
  linkedin: string,
  resumeUrl?: string,
  promptItems: ScreeningPromptItem[] = [],
  videoFiles: Record<number, File> = {}
): ProfileCompletionResult {
  if (!draft) {
    return { percent: 0, breakdown: [], suggestions: [], isFullyComplete: false };
  }

  const user = buildUserForOnboardingScoring(sessionUser, draft, targetRole, linkedin, resumeUrl);
  const portfolio = buildPortfolioForOnboardingScoring(draft);
  const prompts = buildPromptsForOnboardingScoring(promptItems, videoFiles);
  // Score from the in-memory draft only. The stored profile_completion_percentage is
  // still 0 until save/finalize and must not override this preview.
  return computeProfileCompletionScore(user, prompts, portfolio);
}
